// Camp Details Modal Component
// Detailed view and editing interface for camp email data

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  X, 
  Calendar, 
  MapPin, 
  DollarSign, 
  User, 
  Mail, 
  Clock,
  Heart, 
  Bookmark, 
  EyeOff,
  CheckCircle,
  AlertTriangle,
  ExternalLink,
  Save
} from 'lucide-react';
import Icon from '@/components/ui/Icon';
import { updateCampStatus } from '@/services/campEmailService';

/**
 * Camp Details Modal Component
 * Provides detailed view and editing capabilities for camp email data
 */
const CampDetailsModal = ({ campEmail, isOpen, onClose, onStatusChange }) => {
  const [userNotes, setUserNotes] = useState(campEmail?.user_notes || '');
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Update notes when campEmail changes
  useEffect(() => {
    setUserNotes(campEmail?.user_notes || '');
    setHasChanges(false);
  }, [campEmail]);

  /**
   * Format date range
   */
  const formatDateRange = (startDate, endDate) => {
    if (!startDate) return 'Not specified';
    
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : null;
    
    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', { 
        weekday: 'long',
        month: 'long', 
        day: 'numeric',
        year: 'numeric'
      });
    };

    if (end && start.getTime() !== end.getTime()) {
      return `${formatDate(start)} - ${formatDate(end)}`;
    }
    
    return formatDate(start);
  };

  /**
   * Format email date
   */
  const formatEmailDate = (date) => {
    const emailDate = new Date(date);
    return emailDate.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Handle status change
   */
  const handleStatusChange = async (status) => {
    if (isSaving) return;
    
    setIsSaving(true);
    try {
      await onStatusChange(campEmail.id, status, userNotes);
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle notes change
   */
  const handleNotesChange = (value) => {
    setUserNotes(value);
    setHasChanges(value !== (campEmail?.user_notes || ''));
  };

  /**
   * Save notes
   */
  const saveNotes = async () => {
    if (isSaving || !hasChanges) return;
    
    setIsSaving(true);
    try {
      await onStatusChange(campEmail.id, campEmail.user_status, userNotes);
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving notes:', error);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Open email in Gmail
   */
  const openEmailInGmail = () => {
    if (campEmail?.email_id) {
      const gmailUrl = `https://mail.google.com/mail/u/0/#inbox/${campEmail.email_id}`;
      window.open(gmailUrl, '_blank');
    }
  };

  /**
   * Get confidence score display
   */
  const getConfidenceScore = () => {
    const confidence = campEmail?.extraction_confidence || 0;
    const percentage = Math.round(confidence * 100);
    
    let color = 'text-red-600';
    let bgColor = 'bg-red-100';
    
    if (confidence >= 0.8) {
      color = 'text-green-600';
      bgColor = 'bg-green-100';
    } else if (confidence >= 0.6) {
      color = 'text-yellow-600';
      bgColor = 'bg-yellow-100';
    }

    return (
      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${bgColor} ${color}`}>
        <AlertTriangle className="h-4 w-4 mr-1" />
        {percentage}% confidence
      </div>
    );
  };

  /**
   * Get status button
   */
  const getStatusButton = (status, icon, label, isActive) => (
    <Button
      variant={isActive ? "default" : "outline"}
      size="sm"
      onClick={() => handleStatusChange(status)}
      disabled={isSaving}
      className={`${
        isActive 
          ? status === 'favorited' ? 'bg-red-600 hover:bg-red-700' :
            status === 'saved_for_later' ? 'bg-blue-600 hover:bg-blue-700' :
            'bg-gray-600 hover:bg-gray-700'
          : ''
      }`}
    >
      {icon}
      {label}
    </Button>
  );

  if (!campEmail) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-xl font-bold text-gray-900 truncate">
                {campEmail.camp_name || campEmail.email_subject || 'Camp Details'}
              </DialogTitle>
              <div className="flex items-center gap-2 mt-2">
                {getConfidenceScore()}
                {campEmail.needs_manual_review && (
                  <Badge variant="destructive" className="bg-orange-100 text-orange-800">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Needs Review
                  </Badge>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="ml-4"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Extracted Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Extracted Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium text-gray-700">Camp Name</Label>
                  <p className="text-gray-900">
                    {campEmail.camp_name || 'Not found'}
                  </p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium text-gray-700">Organization</Label>
                  <p className="text-gray-900">
                    {campEmail.camp_organization || 'Not found'}
                  </p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium text-gray-700">Dates</Label>
                  <p className="text-gray-900">
                    {formatDateRange(campEmail.camp_dates_start, campEmail.camp_dates_end)}
                  </p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium text-gray-700">Location</Label>
                  <p className="text-gray-900">
                    {campEmail.camp_location ? (
                      <>
                        {campEmail.camp_location}
                        {campEmail.camp_address && (
                          <span className="block text-sm text-gray-600">
                            {campEmail.camp_address}
                          </span>
                        )}
                      </>
                    ) : 'Not found'}
                  </p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium text-gray-700">Cost</Label>
                  <p className="text-gray-900">
                    {campEmail.camp_cost ? `$${campEmail.camp_cost}` : 'Not specified'}
                  </p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium text-gray-700">Child</Label>
                  <p className="text-gray-900">
                    {campEmail.child_name || 'Not specified'}
                  </p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium text-gray-700">Registration Deadline</Label>
                  <p className="text-gray-900">
                    {campEmail.registration_deadline ? 
                      new Date(campEmail.registration_deadline).toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      }) : 'Not specified'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Original Email */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Original Email</h3>
            <div className="p-4 bg-gray-50 rounded-lg space-y-2">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-gray-600" />
                <span className="font-medium">From:</span>
                <span>{campEmail.sender_name || campEmail.sender_email}</span>
                <span className="text-gray-500">({campEmail.sender_email})</span>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="font-medium">Subject:</span>
                <span className="text-gray-900">{campEmail.email_subject}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-600" />
                <span className="font-medium">Date:</span>
                <span>{formatEmailDate(campEmail.email_date)}</span>
              </div>
              
              <div className="pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openEmailInGmail}
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  View Full Email in Gmail
                </Button>
              </div>
            </div>
          </div>

          {/* User Actions */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Your Actions</h3>
            
            <div className="flex flex-wrap gap-2">
              {getStatusButton(
                'favorited',
                <Heart className="h-4 w-4 mr-2" />,
                'Add to Favorites',
                campEmail.user_status === 'favorited'
              )}
              
              {getStatusButton(
                'saved_for_later',
                <Bookmark className="h-4 w-4 mr-2" />,
                'Save for Later',
                campEmail.user_status === 'saved_for_later'
              )}
              
              {getStatusButton(
                'hidden',
                <EyeOff className="h-4 w-4 mr-2" />,
                'Hide This Camp',
                campEmail.user_status === 'hidden'
              )}
              
              {getStatusButton(
                'reviewed',
                <CheckCircle className="h-4 w-4 mr-2" />,
                'Mark as Reviewed',
                campEmail.user_status === 'reviewed'
              )}
            </div>
          </div>

          {/* User Notes */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Your Notes</h3>
            <div className="space-y-2">
              <Textarea
                value={userNotes}
                onChange={(e) => handleNotesChange(e.target.value)}
                placeholder="Add your notes about this camp..."
                rows={4}
                className="resize-none"
              />
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  {userNotes.length} characters
                </span>
                <Button
                  size="sm"
                  onClick={saveNotes}
                  disabled={!hasChanges || isSaving}
                  className="flex items-center gap-2"
                >
                  {isSaving ? (
                    <Icon name="loader-circle" size="sm" className="animate-spin text-white" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {hasChanges ? 'Save Notes' : 'Saved'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CampDetailsModal;
